import { useQuery } from '@tanstack/react-query'
import { getAuditLog } from '../../services/api'
import { ACTION_LABELS, ACTION_COLORS, humaniseActionType } from '../../constants/activityLabels'
import type { AuditEntry } from '../../types'

interface ProjectActivityTimelineProps {
  projectId: string
}

function formatLabel(entry: AuditEntry): string {
  const base = ACTION_LABELS[entry.action_type] || humaniseActionType(entry.action_type)
  const meta = entry.metadata
  if (!meta) return base

  if (entry.action_type === 'permission.grant' && meta.grantee_email) {
    return `Shared with ${meta.grantee_email}`
  }
  if (entry.action_type === 'document_list.add' && meta.title) {
    return `Saved: "${meta.title}"`
  }
  if (entry.action_type === 'document.download' && meta.user_email) {
    return `Downloaded by ${meta.user_email}`
  }

  return base
}

export default function ProjectActivityTimeline({ projectId }: ProjectActivityTimelineProps) {
  // BUG-NEW-R: use project_id param (not resource_id) so the backend expands
  // to all doc-level resource_ids — upload/correction/embedding events appear.
  const { data, isLoading } = useQuery({
    queryKey: ['project-audit', projectId],
    queryFn: () => getAuditLog({ project_id: projectId, limit: 20 }),
    retry: 1,
    enabled: !!projectId,
  })

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="w-3.5 h-3.5 rounded-full bg-gray-200 mt-0.5 animate-pulse" />
            <div className="flex-1 space-y-1">
              <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse" />
              <div className="h-2 bg-gray-100 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  const entries = data?.events?.slice(0, 20) || []

  if (!entries.length) {
    return <p className="text-xs text-gray-500">No project activity recorded</p>
  }

  return (
    <div className="space-y-0 relative">
      {entries.map((entry, i) => {
        const color = ACTION_COLORS[entry.action_type] || 'bg-gray-400'
        return (
          <div key={entry.event_id || i} className="flex gap-3 relative">
            {i < entries.length - 1 && (
              <div className="absolute left-[6px] top-5 bottom-0 w-px bg-gray-100" />
            )}
            <div className={`w-3.5 h-3.5 rounded-full ${color} border-2 border-white mt-0.5 flex-shrink-0 relative z-10`} />
            <div className="pb-3">
              <p className="text-xs text-gray-700">{formatLabel(entry)}</p>
              <p className="text-xs text-gray-500">
                {new Date(entry.timestamp).toLocaleDateString('en-AU', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney',
                })}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
